import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Row, Col, Container, Spinner, Card } from "react-bootstrap";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import * as utilFields from "../../../utils/utils";
import FormInput from "../../../components/ui/FormInput";
import * as schemas from "../../../utils/validationSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { BASE_URL } from "../../../env";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import Sidebar from "../../../components/layout/Sidebar"; // Import the custom styling
import "./AddEditEntityDetails.css";

const AddEditEntityDetails = () => {
  const location = useLocation();
  const { type, id, entity } = location.state || {};

  const [entityData, setEntityData] = useState({});
  const [fieldsData, setFieldsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modelName, setModelName] = useState("");
  const [validationSchema, setValidationSchema] = useState();
  const [viewNavPath, setViewNavPath] = useState("");
  const [editNavPath, setEditNavPath] = useState("");
  const [title, setTitle] = useState("");
  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [isImageExist, setIsImageExist] = useState(false);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 992);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  // Update the data, fields, and schema based on the entity
  useEffect(() => {
    const entityConfig = {
      fundingAgencies: {
        initialData: utilFields.initialFundingAgencyDetails,
        fields: utilFields.fundingAgencyDetailsFields,
        validationSchema: schemas.fundingAgencyDetailsValidationSchema,
        model: "funding_agencies",
        viewNavigationPath: "/funding/agency/list",
        editNavigationPath: "/addEditFundingAgency",
        title: "Funding Agency Details",
        isImageExist: false,
      },
      dailyGoldRate: {
        initialData: utilFields.initialDailyGoldrate,
        fields: utilFields.dailyGoldRateDetailsFields,
        validationSchema: schemas.dailyGoldRateDetailsValidationSchema,
        model: "daily_gold_rate",
        viewNavigationPath: "/dailyGoldRate",
        editNavigationPath: "/addEditDailyGoldRate",
        title: "Daily Gold Rate",
        isImageExist: false,
      },
    };
    if (entityConfig[entity]) {
      const {
        initialData,
        fields,
        validationSchema,
        model,
        viewNavigationPath,
        editNavigationPath,
        title,
        isImageExist,
      } = entityConfig[entity];
      if (type === "Add") {
        setEntityData(initialData);
      }
      console.log("fields: " + JSON.stringify(fields));
      setFieldsData(fields);
      setValidationSchema(validationSchema);
      setModelName(model);
      setViewNavPath(viewNavigationPath);
      setEditNavPath(editNavigationPath);
      setTitle(title);
      setIsImageExist(isImageExist);
    } else {
      setEntityData({});
      setFieldsData([]);
      setValidationSchema(null);
      setModelName("");
      setViewNavPath("");
      setEditNavPath("");
      setTitle("");
      setIsImageExist(false);
    }
    setLoading(false);
  }, [entity, type]);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema), // Fallback to default schema
    defaultValues: entityData,
  });

  useEffect(() => {
    const fetchEntityData = async (id, modelName) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getModelData/${modelName}?filter={"id":${id}}`
        );
        const fetchedData = response.data.data[0];
        setEntityData(fetchedData);
        setFilePreviews({});
      } catch (error) {
        console.error("Error fetching details:", error);
      } finally {
        setLoading(false);
      }
    };

    if ((type === "View" || type === "Edit") && modelName) {
      setLoading(true);
      fetchEntityData(id, modelName);
    } else {
      setLoading(false);
    }
  }, [type, id, modelName, entity]);

  useEffect(() => {
    if (entityData) {
      Object.keys(entityData).forEach((key) => {
        setValue(key, entityData[key]);
        if (isImageExist) {
          if (key.endsWith("Image") && entityData[key]) {
            setExistingFiles((prev) => ({
              ...prev,
              [key]: `${BASE_URL}/images/${entityData[key]}`, // Construct the image URL
            }));
          }
        }
      });
    }
  }, [entityData, setValue, isImageExist]);

  const onSubmit = async (data) => {
    try {
      let response;
      if (!isImageExist) {
        const payload = {
          ...data,
          model: modelName,
          type,
        };

        response = await axios.post(`${BASE_URL}/api/addDetails`, payload);
      }
      if (isImageExist) {
        const formData = new FormData();

        Object.entries(data).forEach(([key, value]) => {
          if (filePreviews[key]?.file) {
            formData.append(key, filePreviews[key].file);
          } else {
            // Append other non-file fields
            if (value !== null && value !== "") {
              formData.append(key, value);
            }
          }
        });

        formData.append("model", modelName);
        formData.append("type", type);

        response = await axios.post(
          `${BASE_URL}/api/addDetailsWithImages`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: true, // If cookies are needed
          }
        );
      }

      if (response.status === 201) {
        setEntityData(response.data.savedData);
        console.log("Details added successfully: ", response.data);
        if (type === "Add") {
          navigate(viewNavPath, { state: { entity: entity } });
        }
        if (type === "Edit") {
          navigate(editNavPath, {
            state: { type: "View", id, entity },
          });
        }
      } else {
        console.log("Error Updating Details: ", response.data.error);
      }
    } catch (error) {
      if (error.response) {
        // The request was made, and the server responded with a status code
        // that falls out of the range of 2xx
        console.error("Error response:", error.response.data);

        if (
          error.response.status === 400 &&
          error.response.data.error === "Unique constraint violation."
        ) {
          alert(
            `Error: ${error.response.data.error
            }\nDetails: ${error.response.data.details.join(", ")}`
          );
        } else {
          alert(`An error occurred: ${error.response.data.error}`);
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error("Error request:", error.request);
        alert("No response from the server. Please try again later.");
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("Error message:", error.message);
        alert("An error occurred. Please try again.");
      }
    }
  };
  if (loading) {
    return (
      <div className="overlay">
        <div className="spinner-container">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  const renderConfigSection = (sectionTitle, prefix, colorClass) => {
    const sectionFields = fieldsData.filter(f => f.name.startsWith(prefix));
    return (
      <div className="section-container shadow-sm">
        <h5 className={`section-title ${colorClass}`}>{sectionTitle}</h5>
        <Row>
          {sectionFields.map((field, index) => {
            const isCheckbox = field.type === "checkbox";
            const processedLabel = field.label
              .replace(/^(BL|JLG|GPL|DL)\s+/i, "")
              .toUpperCase();

            return (
              <Col xs={12} sm={6} lg={3} key={index} className={`mb-3 ${isCheckbox ? 'centered-field' : ''}`}>
                <div className="field-wrapper w-100">
                  <FormInput
                    label={processedLabel}
                    type={field.type}
                    name={field.name}
                    register={register}
                    errors={errors}
                    options={field.options || []}
                    readOnly={type === "View" || field.readOnly}
                    required={field.required}
                  />
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  };

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div
            className="funding-agency-details-container"
            style={{ overflowY: "auto" }}
          >
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 pb-2 border-bottom gap-3">
              <h2 className="text-center m-0 flex-grow-1 header-title">
                {title}
              </h2>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-secondary"
                  className="btn-sm"
                  // The list page reads which list to show from state.entity;
                  // without it the list stayed on "Loading..." forever.
                  onClick={() =>
                    navigate(viewNavPath || "/funding/agency/list", {
                      state: { entity },
                    })
                  }
                >
                  Back to List
                </Button>
                {type === "View" && (
                  <Button
                    className="btn btn-primary btn-sm"
                    onClick={() =>
                      navigate(editNavPath, {
                        state: { type: "Edit", id, entity },
                      })
                    }
                  >
                    Edit
                  </Button>
                )}
              </div>
            </div>

            <Form onSubmit={handleSubmit(onSubmit)}>
              {entity === "fundingAgencies" ? (
                <>
                  {/* Basic Information Section */}
                  <div className="section-container shadow-sm">
                    <h5 className="section-title basic-info-title">Basic Information</h5>
                    <Row className="justify-content-center">
                      {fieldsData.filter(f => ['agencyName', 'bcOrOwn', 'fundingAgencyCode'].includes(f.name)).map((field, index) => (
                        <Col xs={12} sm={6} md={4} key={index} className="mb-3">
                          <FormInput
                            label={field.label.toUpperCase()}
                            type={field.type}
                            name={field.name}
                            register={register}
                            errors={errors}
                            options={field.options || []}
                            readOnly={type === "View" || field.readOnly}
                            required={field.required}
                          />
                        </Col>
                      ))}
                    </Row>
                  </div>

                  {/* Business Loan Section */}
                  {renderConfigSection("Business Loan (BL) Configuration", "bl", "bl-info-title")}

                  {/* JLG Loan Section */}
                  {renderConfigSection("JLG Loan Configuration", "jlg", "jlg-info-title")}

                  {/* GPL Loan Section */}
                  {renderConfigSection("GPL Loan Configuration", "gpl", "gpl-info-title")}

                  {/* Daily Loan Section. Without this the dl* columns had no
                      screen at all, so every agency's DL charge percentages sat
                      NULL and DL charges computed to zero. */}
                  {renderConfigSection("Daily Loan (DL) Configuration", "dl", "dl-info-title")}
                </>
              ) : (
                <Row className="justify-content-center">
                  <Col xs={12} lg={8}>
                    <Card className="p-4 shadow-sm">
                      <Row>
                        {fieldsData.map((field, index) => (
                          <Col
                            xs={12}
                            md={field.type === "file" ? 12 : 6}
                            key={index}
                            className="mb-3"
                          >
                            <FormInput
                              label={field.label}
                              type={field.type}
                              name={field.name}
                              register={register}
                              errors={errors}
                              options={field.options || []}
                              readOnly={type === "View" || field.readOnly}
                              handleFileChange={(e) =>
                                utilFields.handleFileChange(
                                  e,
                                  field.name,
                                  setFilePreviews
                                )
                              }
                              existingFileUrl={existingFiles[field.name] || ""}
                              filePreview={filePreviews[field.name]?.previewUrl || ""}
                              required={field.required}
                            />
                          </Col>
                        ))}
                      </Row>
                    </Card>
                  </Col>
                </Row>
              )}
              <div className="text-center mt-5 pb-4">
                {!(type === "View") && (
                  <Button variant="success" type="submit" className="px-5 py-2 shadow-sm" style={{ borderRadius: '50px' }}>
                    {type === "Edit" ? "UPDATE DETAILS" : "SUBMIT DETAILS"}
                  </Button>
                )}{" "}
                {type === "Edit" && (
                  <Button
                    variant="warning"
                    className="ms-3 px-5 py-2 shadow-sm"
                    style={{ borderRadius: '50px' }}
                    onClick={() =>
                      navigate(editNavPath, {
                        state: { type: "View", id, entity },
                      })
                    }
                  >
                    CANCEL
                  </Button>
                )}
              </div>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AddEditEntityDetails;
